import React, {useEffect, useMemo, useState} from "react";
import {Box, Button, DialogActions, DialogContent, Grid, MenuItem, TextField, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import AlertDialog from "../alert-dialog";
import {getCsvModeOptions} from "./constants";
import {appConfigSelector} from "../../state/app-config/appConfigSelectors";
import {appSelector} from "../../state/app/appSelectors";
import {setEtlWizardCsvFormat} from "../../state/package-manager/packageManagerActions";
import {localizeI18nObj} from "../../utils/i18n";

const normalizeCsvFormatOptions = (values, language, languages) =>
  (values || []).reduce((acc, item) => {
    if (typeof item === "string") {
      acc.push({value: item, label: item});
      return acc;
    }

    if (item && typeof item === "object" && typeof item.value === "string") {
      acc.push({
        value: item.value,
        label: localizeI18nObj(item.label, language, languages) ?? item.value
      });
    }

    return acc;
  }, []);
const EtlWizardCsvFormatDialog = ({open, csvFormat, onClose, onConfirm}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const appConfig = useSelector(appConfigSelector);
  const {language, languages} = useSelector(appSelector);

  const [submitted, setSubmitted] = useState(false);

  const csvDelimiterOptions = useMemo(
    () => normalizeCsvFormatOptions(appConfig?.etl?.csvDelimiterValues, language, languages),
    [appConfig?.etl?.csvDelimiterValues, language, languages]
  );
  const csvQuoteOptions = useMemo(
    () => normalizeCsvFormatOptions(appConfig?.etl?.csvQuoteValues, language, languages),
    [appConfig?.etl?.csvQuoteValues, language, languages]
  );
  const csvEscapeOptions = useMemo(
    () => normalizeCsvFormatOptions(appConfig?.etl?.csvEscapeValues, language, languages),
    [appConfig?.etl?.csvEscapeValues, language, languages]
  );
  const csvModeOptions = useMemo(() => getCsvModeOptions(t), [t]);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
    }
  }, [open]);

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  const handleSubmit = event => {
    event.preventDefault();
    setSubmitted(true);

    if (!csvFormat?.delimiter) {
      return;
    }

    setSubmitted(false);
    onConfirm();
  };

  return (
    <AlertDialog title={t("modules.packageManager.etlWizard.csvFormatDialog.title")} open={open} onClose={handleClose}>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <DialogContent className="etl-wizard__csv-format-dialog" sx={{paddingTop: 0}}>
          <Typography sx={{marginBottom: "24px", whiteSpace: "pre-line"}}>
            {t("modules.packageManager.etlWizard.csvFormatDialog.description")}
          </Typography>
          <Grid container rowSpacing={1} columnSpacing={2}>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                label={t("modules.packageManager.etlWizard.csvFormatDialog.fields.delimiter.label")}
                value={csvFormat.delimiter ?? ""}
                error={submitted && !csvFormat.delimiter}
                helperText={submitted && !csvFormat.delimiter ? t("commons.validation.required") : " "}
                onChange={event => {
                  dispatch(setEtlWizardCsvFormat({...csvFormat, delimiter: event.target.value || null}));
                }}
                variant="outlined"
                required
              >
                {csvDelimiterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                label={t("modules.packageManager.etlWizard.csvFormatDialog.fields.quote.label")}
                value={csvFormat.quote ?? ""}
                onChange={event => {
                  dispatch(setEtlWizardCsvFormat({...csvFormat, quote: event.target.value || null}));
                }}
                variant="outlined"
              >
                {csvQuoteOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                label={t("modules.packageManager.etlWizard.csvFormatDialog.fields.escape.label")}
                value={csvFormat.escape ?? ""}
                onChange={event => {
                  dispatch(setEtlWizardCsvFormat({...csvFormat, escape: event.target.value || null}));
                }}
                variant="outlined"
              >
                {csvEscapeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                label={t("modules.packageManager.etlWizard.csvFormatDialog.fields.csvMode.label")}
                value={csvFormat.csvMode}
                onChange={event => {
                  dispatch(setEtlWizardCsvFormat({...csvFormat, csvMode: event.target.value}));
                }}
                variant="outlined"
                required
              >
                {csvModeOptions.map(({id, label}) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={handleClose}>
            {t("commons.confirm.cancel")}
          </Button>
          <Button type="submit">{t("commons.confirm.confirm")}</Button>
        </DialogActions>
      </Box>
    </AlertDialog>
  );
};

export default EtlWizardCsvFormatDialog;
